import { useMemo, useState } from "react";
import { Badge, Filter, RigoFilters, SectionCard } from "@/deps/ui-library";
import type { ReportExport, YearChecklist } from "@/api/types";
import { DownloadMenu } from "@/features/reports/DownloadMenu";
import { formatDate } from "@/lib/dates";

type Entry = YearChecklist["log"][number];

const ALL = "all";
const OUTCOMES = [
  { value: ALL, label: "All outcomes" },
  { value: "done", label: "Done" },
  { value: "refused", label: "Refused" },
];

/**
 * Which run of the close each entry belongs to. A run ends when the year closes, so the next thing logged
 * belongs to the next version. The log is newest first, so this walks it from the end.
 */
export function withRuns(log: Entry[]): Array<Entry & { run: number }> {
  let run = 1;
  const oldestFirst = [...log].reverse().map((entry) => {
    const entered = { ...entry, run };
    if (entry.action === "close_year" && !entry.refused) run += 1;
    return entered;
  });
  return oldestFirst.reverse();
}

/** The close log: filters narrow what's on screen, and the export always has every event. */
export function YearCloseLog({ code, log }: { code: string; log: Entry[] }) {
  const entries = useMemo(() => withRuns(log), [log]);
  const [actor, setActor] = useState(ALL);
  const [outcome, setOutcome] = useState(ALL);
  const [run, setRun] = useState(ALL);

  const actors = useMemo(
    () => [
      { value: ALL, label: "All people" },
      ...[...new Set(entries.map((e) => e.loggedBy).filter(Boolean))].map((name) => ({
        value: name,
        label: name,
      })),
    ],
    [entries],
  );
  const runs = useMemo(
    () => [
      { value: ALL, label: "All versions" },
      ...[...new Set(entries.map((e) => e.run))]
        .sort((a, b) => a - b)
        .map((value) => ({ value: String(value), label: `Version ${value}` })),
    ],
    [entries],
  );

  const shown = entries.filter(
    (entry) =>
      (actor === ALL || entry.loggedBy === actor) &&
      (outcome === ALL || entry.refused === (outcome === "refused")) &&
      (run === ALL || String(entry.run) === run),
  );

  const exportable = (): ReportExport => ({
    title: "Close log",
    period: code,
    columns: [
      { header: "When" },
      { header: "Who" },
      { header: "Version" },
      { header: "What" },
      { header: "Outcome" },
    ],
    rows: entries.map((entry) => [
      entry.loggedAt,
      entry.loggedBy,
      String(entry.run),
      entry.detail,
      entry.refused ? "Refused" : "Done",
    ]),
  });

  const filter = (
    label: string,
    items: Array<{ value: string; label: string }>,
    value: string,
    set: (value: string) => void,
  ) => (
    <Filter.SingleSelect
      label={label}
      icon={false}
      searchable={false}
      clearable={false}
      items={items}
      value={items.find((item) => item.value === value) ?? null}
      onValueChange={(item) => set(item ? String(item.value) : ALL)}
    />
  );

  return (
    <SectionCard
      title="Close log"
      flush
      actions={
        <DownloadMenu
          title={`Close log ${code}`}
          exportable={entries.length ? exportable : undefined}
        />
      }
    >
      {entries.length === 0 ? (
        <p className="text-fg-muted p-4 text-sm">Nothing has been logged for this year.</p>
      ) : (
        <>
          <div className="border-border border-b px-4 py-2.5">
            <RigoFilters.Root>
              <RigoFilters.StaticRow>
                {filter("Person", actors, actor, setActor)}
                {filter("Outcome", OUTCOMES, outcome, setOutcome)}
                {filter("Version", runs, run, setRun)}
              </RigoFilters.StaticRow>
            </RigoFilters.Root>
          </div>
          {shown.length === 0 ? (
            <p className="text-fg-muted p-4 text-sm">Nothing logged matches these filters.</p>
          ) : (
            <ul className="flex flex-col">
              {shown.map((entry) => (
                <li
                  key={entry.id}
                  className="border-border flex flex-wrap items-baseline justify-between gap-3 border-b px-4 py-2.5 text-sm last:border-b-0"
                >
                  <span className="text-fg min-w-0">
                    {entry.detail}
                    {entry.refused ? (
                      <Badge variant="soft" size="sm" colorScheme="danger" className="ml-2">
                        Refused
                      </Badge>
                    ) : null}
                  </span>
                  <span className="text-fg-muted text-xs">
                    {entry.loggedBy ? `${entry.loggedBy} · ` : ""}
                    {formatDate(entry.loggedAt.slice(0, 10))} · Version {entry.run}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </SectionCard>
  );
}
